import * as B from '@blueprintjs/core';
import {Elements, PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js';
import type {StripePaymentElementChangeEvent} from '@stripe/stripe-js';
import React from 'react';

import type {ApiStripeClientSecret} from 'app/modules/Remote/Organization';
import {STRIPE_MINIMUM_CHARGE_CENTS, getStripePromise} from 'app/utils/stripeUtils';

import {STRIPE_APPEARANCE, STRIPE_FONTS} from '../appearance';
import {
  BILLING_NOT_CONFIGURED_MESSAGE,
  PAYMENT_OPTIONS_LOAD_ERROR,
  useCartCustomerSession,
} from '../hooks';
import cs from './styles.module.scss';

const FALLBACK_ERROR = 'We couldn’t use that payment method. Try another one.';

/** Where the mounted element is. Until it's `ready` there is no card to tokenize. */
export type CartPaymentStatus = 'loading' | 'ready' | 'failed';

/** The card a confirmation token charges, as Stripe's preview of it reports. */
export interface TokenizedCard {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export interface CartPaymentToken {
  id: string;
  /** Null when the token isn't for a card. */
  card: TokenizedCard | null;
}

/**
 * A token, or why there isn't one. The element shows `error` itself too, except for a field-level
 * validation error, which Stripe shows beside the field; a caller that has the element collapsed
 * needs it passed back to show anywhere.
 */
export type CartTokenizeResult =
  | {ok: true; token: CartPaymentToken}
  | {ok: false; error: string | null};

/**
 * What the cart page drives the mounted element with. The element lives inside `<Elements>`, so the
 * Stripe and Elements objects it needs only exist below that boundary — this hands them back up.
 */
export interface CartPaymentHandle {
  /**
   * Validates the form and tokenizes whichever card the user picked or typed, save choice included.
   * Not ok means the user has something to fix, and the element is already saying what.
   */
  createConfirmationToken: () => Promise<CartTokenizeResult>;
  /** Finishes a 3D Secure challenge the server asked for. Returns the message on failure. */
  handleNextAction: (clientSecret: string) => Promise<string | null>;
}

const CartPaymentForm: React.FunctionComponent<{
  handleRef: React.MutableRefObject<CartPaymentHandle | null>;
  onStatusChange: (status: CartPaymentStatus) => void;
  onChange?: (event: StripePaymentElementChangeEvent) => void;
  onFocus?: () => void;
}> = ({handleRef, onStatusChange, onChange, onFocus}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    handleRef.current = {
      createConfirmationToken: async () => {
        setError(null);
        if (!stripe || !elements) {
          setError(FALLBACK_ERROR);
          return {ok: false, error: FALLBACK_ERROR};
        }

        const submitted = await elements.submit();
        if (submitted.error) {
          // Stripe renders a field-level validation error in the element itself, so repeating it
          // below would say the same thing twice. Anything else has no other place to appear.
          if (submitted.error.type !== 'validation_error') {
            setError(submitted.error.message ?? FALLBACK_ERROR);
          }
          return {ok: false, error: submitted.error.message ?? null};
        }

        const {error: tokenError, confirmationToken} = await stripe.createConfirmationToken({
          elements,
        });
        if (tokenError || !confirmationToken) {
          const message = tokenError?.message ?? FALLBACK_ERROR;
          setError(message);
          return {ok: false, error: message};
        }
        const card = confirmationToken.payment_method_preview?.card;
        return {
          ok: true,
          token: {
            id: confirmationToken.id,
            card: card
              ? {
                  brand: card.brand,
                  last4: card.last4,
                  expMonth: card.exp_month,
                  expYear: card.exp_year,
                }
              : null,
          },
        };
      },

      handleNextAction: async (clientSecret) => {
        if (!stripe) {
          setError(FALLBACK_ERROR);
          return FALLBACK_ERROR;
        }
        const {error: actionError} = await stripe.handleNextAction({clientSecret});
        if (actionError) {
          const message = actionError.message ?? FALLBACK_ERROR;
          setError(message);
          return message;
        }
        setError(null);
        return null;
      },
    };

    return () => {
      handleRef.current = null;
    };
  }, [stripe, elements, handleRef]);

  // A new customer session remounts the element, which has to load all over again.
  React.useEffect(() => () => onStatusChange('loading'), [onStatusChange]);

  return (
    <>
      <PaymentElement
        options={{layout: 'tabs'}}
        onReady={() => onStatusChange('ready')}
        onChange={onChange}
        onFocus={onFocus}
        onLoadError={() => {
          setError(PAYMENT_OPTIONS_LOAD_ERROR);
          onStatusChange('failed');
        }}
      />
      {/* Kept mounted so a screen reader announces a decline that arrives after Submit. */}
      <div role="status">{error && <div className={cs.error}>{error}</div>}</div>
    </>
  );
};

const PaymentSection: React.FunctionComponent<{children: React.ReactNode}> = ({children}) => (
  <section className={cs.section} aria-labelledby="cart-payment-heading">
    <h3 id="cart-payment-heading" className={cs.heading}>
      Payment
    </h3>
    {children}
  </section>
);

/**
 * The payment method picker in the cart's order summary. Stripe renders the organization's saved
 * cards and bank accounts, the forms for a new one and the save checkbox from the customer session;
 * nothing here lists or stores a payment method. An unsaved one is charged through the confirmation
 * token and never attaches to the customer, which is what lets someone pay with a personal card.
 */
export const CartPaymentElement: React.FunctionComponent<{
  organizationId: string;
  /**
   * The cart subtotal, which is what the element displays on its button. Tax is added when the
   * invoice is finalized, so the charge can exceed this and confirmation still succeeds.
   */
  amountCents: number;
  handleRef: React.MutableRefObject<CartPaymentHandle | null>;
  onStatusChange: (status: CartPaymentStatus) => void;
  /** Which payment method the buyer has picked, as Stripe reports it. */
  onChange?: (event: StripePaymentElementChangeEvent) => void;
  /** The buyer moved into the element, by keyboard or pointer. */
  onFocus?: () => void;
}> = ({organizationId, amountCents, handleRef, onStatusChange, onChange, onFocus}) => {
  // Shares React Query's cache with the cart page, which reads the same hook to block Submit.
  const sessionQuery = useCartCustomerSession(organizationId, true);

  if (sessionQuery.isPending) {
    return (
      <PaymentSection>
        <div className={cs.loading}>
          <B.Spinner size={20} />
        </div>
      </PaymentSection>
    );
  }

  if (sessionQuery.isError || sessionQuery.data.kind === 'notConfigured') {
    return (
      <PaymentSection>
        <B.Callout intent={B.Intent.WARNING} icon="warning-sign">
          {sessionQuery.isError ? PAYMENT_OPTIONS_LOAD_ERROR : BILLING_NOT_CONFIGURED_MESSAGE}
        </B.Callout>
      </PaymentSection>
    );
  }

  const session: ApiStripeClientSecret = sessionQuery.data.session;

  return (
    <PaymentSection>
      <Elements
        // A different customer session means a different Elements tree; Stripe can't swap the
        // session secret in place the way it can the amount.
        key={session.clientSecret}
        stripe={getStripePromise(session.stripeMode)}
        options={{
          mode: 'payment',
          // Pinning the payment method types below makes Stripe check this against their minimum
          // charge, and a smaller amount fails the session load outright. What we actually
          // charge is the invoice, not this, and Submit is blocked under the minimum anyway.
          amount: Math.max(amountCents, STRIPE_MINIMUM_CHARGE_CENTS),
          currency: 'usd',
          paymentMethodTypes: ['card', 'us_bank_account'],
          // A bank account added here is verified by logging in to the bank, so the order can be
          // placed on it right away rather than after days of microdeposits.
          paymentMethodOptions: {us_bank_account: {verification_method: 'instant'}},
          // The card is tokenized here and charged by the server against the invoice, so Elements
          // must not create the payment method itself.
          paymentMethodCreation: 'manual',
          customerSessionClientSecret: session.clientSecret,
          appearance: STRIPE_APPEARANCE,
          fonts: STRIPE_FONTS,
        }}
      >
        <CartPaymentForm
          handleRef={handleRef}
          onStatusChange={onStatusChange}
          onChange={onChange}
          onFocus={onFocus}
        />
      </Elements>
    </PaymentSection>
  );
};

export default CartPaymentElement;
